import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = params.get("q") || "";
    setQ(term);
    if (!term) { setResults([]); return; }
    setLoading(true);
    api.get("/search", { params: { q: term } }).then(({ data }) => { setResults(data.results); setLoading(false); });
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    setParams(q.trim() ? { q: q.trim() } : {}, { replace: true });
  };

  return (
    <>
      <PageMeta title="Search" />
      <section className="container-dbrf py-16 lg:py-24">
        <p className="eyebrow mb-4">Search</p>
        <h1 className="font-display font-extrabold text-navy" style={{ fontSize: "clamp(2rem,4vw,3rem)" }}>Search the DBRF catalogue.</h1>
        <form onSubmit={submit} className="relative mt-8 max-w-xl">
          <label htmlFor="search-page-input" className="sr-only">Search the DBRF catalogue</label>
          <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--mineral)]" />
          <input id="search-page-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-page-input" placeholder="Capabilities, projects, disciplines…"
            className="w-full rounded-full border border-[var(--line)] bg-[var(--paper)] py-3.5 pl-11 pr-24 text-sm outline-none focus:border-[var(--cobalt)]" />
          <button type="submit" data-testid="search-page-btn" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[var(--navy)] px-4 py-2 text-xs font-semibold text-white btn-transition hover:bg-[var(--cobalt)]">Search</button>
        </form>

        <div className="mt-10">
          {loading && <p className="font-mono text-sm text-[var(--mineral)]">Searching…</p>}
          {!loading && params.get("q") && (
            <p className="mb-6 font-mono text-sm text-[var(--mineral)]" aria-live="polite">{results.length} result{results.length === 1 ? "" : "s"} for “{params.get("q")}”</p>
          )}
          {!loading && params.get("q") && results.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-10 text-center" data-testid="search-page-empty">
              <p className="font-display text-lg text-navy">No results for “{params.get("q")}”.</p>
              <div className="mt-4 flex justify-center gap-4 text-sm font-semibold">
                <button onClick={() => { setQ(""); setParams({}, { replace: true }); }} className="text-[var(--cobalt)] link-underline">Clear the query</button>
                <Link to="/bring-a-challenge" className="text-[var(--cobalt)] link-underline">Submit a challenge</Link>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {results.map((r) => (
              <Link key={r.url + r.title} to={r.url} data-testid="search-page-result" className="card-lift flex items-center justify-between rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5">
                <div>
                  <span className="font-mono text-[0.66rem] uppercase tracking-wide text-[var(--cobalt)]">{r.type}</span>
                  <p className="font-display font-semibold text-navy">{r.title}</p>
                  <p className="text-xs text-[var(--mineral)]">{r.meta}</p>
                </div>
                <ArrowRight size={16} className="text-[var(--mineral)]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
